import { type ServerSettings, type ServerSettingsPatch } from "@cmmd-forge/contracts";
import { resolveCloudSignInActive } from "../../lib/firstAccountSetup";
import {
  normalizeExecutionEnvironmentCapabilities,
  scopeProjectRef,
} from "@cmmd-forge/client-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactElement, useEffect, useRef, useState } from "react";

import { useCmmdWorkIntakeSnapshot } from "../../cmmd/workIntakeResource";
import {
  updateCmmdForgeAccountPreferences,
  useCmmdForgeAccountPreferences,
} from "../../cmmd/forgeAccountPreferences";
import { useCommandPaletteStore } from "../../commandPaletteStore";
import { createCmmdApiClient } from "../../environments/cmmdApi";
import { usePrimaryEnvironmentDescriptor } from "../../environments/primary";
import { useSavedEnvironmentRegistryStore } from "../../environments/runtime";
import { fetchGitHubAgentCapability } from "../../githubAgentCapability";
import { AppTopBarShell } from "../AppTopBar";
import { usePluginConnections } from "../plugins/PluginConnectionsProvider";
import { toastManager } from "../ui/toast";
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { useTheme } from "../../hooks/useTheme";
import { useHandleNewThread } from "../../hooks/useHandleNewThread";
import { getCurrentPrimaryServerApi } from "../../localApi";
import { FIRST_ACCOUNT_SETUP_STARTER_PROMPT_STORAGE_KEY } from "../../lib/firstAccountSetupStarterPrompt";
import {
  COMPLETED_FIRST_ACCOUNT_SETUP_STATE,
  type FirstAccountSetupState,
  type FirstAccountSetupStepId,
  resolveFirstAccountSetupState,
  shouldUseCmmdFirstAccountSetupState,
  writeFirstAccountSetupState,
} from "../../lib/firstAccountSetup";
import { applySettingsUpdated, useServerConfig } from "../../rpc/serverState";
import { useStore } from "../../store";
import { FirstAccountSetup } from "./FirstAccountSetup";
import {
  buildFirstAccountSetupProjects,
  type FirstAccountSetupEnvironmentInput,
} from "./firstAccountSetupProjects";
import {
  FIRST_ACCOUNT_SETUP_STARTER_PROMPT,
  isSetupProjectOpenable,
  type CloudCodexCredentialSummary,
  type ProviderSetupRow,
  type SetupProjectRow,
  type SetupProjectWorkspaceOption,
} from "./firstAccountSetupData";
import {
  openExternalAuthUrl,
  startCodexProviderAuthWithRepair,
  useProviderAuth,
} from "../../hooks/useProviderAuth";
import {
  isCloudProviderSignInEnabled,
  isClaudeCloudSignInEnabled,
  useCloudProviderSignIn,
} from "../../hooks/useCloudProviderSignIn";
import type {
  CmmdClaudeCredentialState,
  CmmdCodexCredentialState,
} from "../../environments/cmmdApi";
import { CloudProviderSignInView } from "../settings/CloudProviderSignInSection";
import { writeStoredValue } from "../../lib/storage";
import { persistFirstAccountSetupProgress } from "./firstAccountSetupProgress";
import { useFirstRunEnvironmentStep } from "./useFirstRunEnvironmentStep";

export { openExternalAuthUrl, startCodexProviderAuthWithRepair };

function buildSetupEnvironments(
  primaryEnvironment: { readonly environmentId: string; readonly label: string } | null | undefined,
  savedEnvironmentsById: Readonly<Record<string, { readonly label: string } | undefined>>,
): readonly FirstAccountSetupEnvironmentInput[] {
  return [
    ...(primaryEnvironment
      ? [
          {
            id: primaryEnvironment.environmentId,
            label: primaryEnvironment.label,
            iconUrl: null,
          },
        ]
      : []),
    ...Object.entries(savedEnvironmentsById).map(([id, environment]) => ({
      id,
      label: environment?.label ?? "Workspace",
      iconUrl: null,
    })),
  ];
}

function buildSetupProjectWorkspaceOptions(input: {
  readonly primaryEnvironment:
    | { readonly environmentId: string; readonly label: string }
    | null
    | undefined;
  readonly cmmdWorkspaces: readonly {
    readonly id: string;
    readonly name: string;
    readonly iconUrl?: string | null;
    readonly logoUrl?: string | null;
  }[];
}): readonly SetupProjectWorkspaceOption[] {
  return [
    ...(input.primaryEnvironment
      ? [
          {
            id: input.primaryEnvironment.environmentId,
            label: input.primaryEnvironment.label,
            iconUrl: null,
          },
        ]
      : []),
    ...input.cmmdWorkspaces.map((workspace) => ({
      id: `cmmd:${workspace.id}`,
      label: workspace.name,
      iconUrl: workspace.iconUrl ?? workspace.logoUrl ?? null,
    })),
  ];
}

function persistFirstAccountSetupStarterPrompt(): void {
  writeStoredValue(
    FIRST_ACCOUNT_SETUP_STARTER_PROMPT_STORAGE_KEY,
    FIRST_ACCOUNT_SETUP_STARTER_PROMPT,
  );
}

function navigateToFirstAccountSetupStarterPrompt(): void {
  persistFirstAccountSetupStarterPrompt();
  if (typeof window === "undefined") {
    return;
  }
  window.location.assign("/new?firstAccountSetupPrompt=1");
}

export function buildFirstAccountSetupCompletionSettingsPatch(input: {
  readonly usageSource: "workspace" | "personal";
  readonly settings: Pick<ServerSettings, "providers">;
  readonly codexAuthenticated: boolean;
}): ServerSettingsPatch {
  const completionPatch: ServerSettingsPatch = {
    firstAccountSetup: COMPLETED_FIRST_ACCOUNT_SETUP_STATE,
  };

  if (input.usageSource === "workspace") {
    return {
      ...completionPatch,
      providers: {
        ...input.settings.providers,
        codex: { ...input.settings.providers.codex, accessMode: "workspace" },
      },
    };
  }

  if (input.codexAuthenticated) {
    return {
      ...completionPatch,
      providers: {
        ...input.settings.providers,
        codex: { ...input.settings.providers.codex, accessMode: "personal" },
      },
    };
  }

  return completionPatch;
}

export async function persistFirstAccountSetupCompletion(input: {
  readonly storage: Storage | undefined;
  readonly patch: ServerSettingsPatch;
  readonly updateServerSettings: (patch: ServerSettingsPatch) => Promise<ServerSettings>;
  readonly onServerSettingsPersisted: (settings: ServerSettings) => void;
  readonly onError: (error: unknown) => void;
}): Promise<boolean> {
  try {
    const nextSettings = await input.updateServerSettings(input.patch);
    writeFirstAccountSetupState(input.storage, COMPLETED_FIRST_ACCOUNT_SETUP_STATE);
    input.onServerSettingsPersisted(nextSettings);
    return true;
  } catch (error) {
    input.onError(error);
    return false;
  }
}

export async function startFirstAccountSetupProjectThread(input: {
  readonly startThread: () => Promise<void>;
  readonly onThreadStarted: () => void;
  readonly onFallback: () => void;
}): Promise<void> {
  try {
    await input.startThread();
    input.onThreadStarted();
  } catch {
    input.onFallback();
  }
}

/** The store credential summary the provider rows read, or null when the flow is off. */
function resolveOnboardingCloudCredential(
  active: boolean,
  connection: CmmdCodexCredentialState | CmmdClaudeCredentialState | null,
): CloudCodexCredentialSummary | null {
  if (!active) return null;
  if (connection?.connected === true) {
    return { connected: true, accountLabel: connection.accountEmail ?? null };
  }
  return { connected: false, accountLabel: null };
}

/**
 * Codex/Claude connect routes to the cloud auth VM on a cloud surface (the local
 * login fails there with unsupported-surface); everything else uses the on-device
 * provider-auth flow.
 */
function routeOnboardingProviderConnect(input: {
  readonly providerId: ProviderSetupRow["id"];
  readonly codexCloudSignInActive: boolean;
  readonly claudeCloudSignInActive: boolean;
  readonly startCloudCodex: () => void;
  readonly startCloudClaude: () => void;
  readonly connectLocal: (providerId: ProviderSetupRow["id"]) => void;
}): void {
  if (input.codexCloudSignInActive && input.providerId === "codex") {
    input.startCloudCodex();
    return;
  }
  if (input.claudeCloudSignInActive && input.providerId === "claudeAgent") {
    input.startCloudClaude();
    return;
  }
  input.connectLocal(input.providerId);
}

export function FirstAccountSetupController(): ReactElement {
  const serverConfig = useServerConfig();
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();
  const queryClient = useQueryClient();
  const primaryEnvironmentDescriptor = usePrimaryEnvironmentDescriptor();
  const useCmmdSetupState = primaryEnvironmentDescriptor
    ? shouldUseCmmdFirstAccountSetupState(primaryEnvironmentDescriptor)
    : false;
  const includeForgeProjects = primaryEnvironmentDescriptor
    ? normalizeExecutionEnvironmentCapabilities(primaryEnvironmentDescriptor).projects.localProjects
    : true;
  const cmmdAccountPreferences = useCmmdForgeAccountPreferences(useCmmdSetupState);
  const { theme, setTheme, uiFontSize, codeFontSize, setUiFontSize, setCodeFontSize } = useTheme();
  const openAddProjectSources = useCommandPaletteStore((store) => store.openAddProjectSources);
  const openAddProjectSourceMode = useCommandPaletteStore(
    (store) => store.openAddProjectSourceMode,
  );
  const { handleNewThread } = useHandleNewThread();
  const environmentStateById = useStore((state) => state.environmentStateById);
  const savedEnvironmentsById = useSavedEnvironmentRegistryStore((state) => state.byId);
  const cmmdWorkIntake = useCmmdWorkIntakeSnapshot();
  const projects = Object.values(environmentStateById).flatMap((environmentState) =>
    environmentState.projectIds.flatMap((projectId) => {
      const project = environmentState.projectById[projectId];
      return project ? [project] : [];
    }),
  );
  const setupState =
    resolveFirstAccountSetupState({
      useCmmdState: useCmmdSetupState,
      cmmdState: cmmdAccountPreferences.data?.firstAccountSetup ?? null,
      serverState: settings.firstAccountSetup,
    }) ?? settings.firstAccountSetup;
  const firstRunEnvironmentStep = useFirstRunEnvironmentStep({
    activeStepId: setupState.activeStepId,
  });
  const pluginConnections = usePluginConnections({
    enabled: setupState.activeStepId === "capabilities",
  });
  const providerAuth = useProviderAuth({ settings, updateSettings });
  const [githubActionBusy, setGitHubActionBusy] = useState(false);
  // One live focus-refetch listener at a time: repeated connect attempts
  // without an intervening window focus would otherwise stack `once` listeners
  // that all fire together on the next focus.
  const githubFocusRefetchListenerRef = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      if (githubFocusRefetchListenerRef.current) {
        window.removeEventListener("focus", githubFocusRefetchListenerRef.current);
        githubFocusRefetchListenerRef.current = null;
      }
    },
    [],
  );
  const codexProvider =
    serverConfig?.providers.find((provider) => provider.provider === "codex") ?? null;
  // Card 1359 — on a non-execution-plane surface (cloud master / cloud shell)
  // local login can't run, so the personal-subscription connect goes through the
  // cloud auth-VM flow and the connected-state comes from the CMMD user store
  // (which the desktop app also syncs into). Same surface gate for codex + claude.
  // Each provider has its own build flag — Claude stays dark until its own flag
  // + the server delivery flag are enabled (see isClaudeCloudSignInEnabled).
  const codexCloudSignInActive = resolveCloudSignInActive({
    descriptor: primaryEnvironmentDescriptor,
    enabled: isCloudProviderSignInEnabled(),
  });
  const claudeCloudSignInActive = resolveCloudSignInActive({
    descriptor: primaryEnvironmentDescriptor,
    enabled: isClaudeCloudSignInEnabled(),
  });
  const codexCloudSignIn = useCloudProviderSignIn({
    enabled: codexCloudSignInActive,
    provider: "codex",
  });
  const claudeCloudSignIn = useCloudProviderSignIn({
    enabled: claudeCloudSignInActive,
    provider: "claudeAgent",
  });
  const cloudCodexCredential = resolveOnboardingCloudCredential(
    codexCloudSignInActive,
    codexCloudSignIn.connection,
  );
  const cloudClaudeCredential = resolveOnboardingCloudCredential(
    claudeCloudSignInActive,
    claudeCloudSignIn.connection,
  );
  const githubCapabilityQuery = useQuery({
    queryKey: ["github-agent-capability"],
    queryFn: fetchGitHubAgentCapability,
    staleTime: 30_000,
  });
  const cmmdProjectsQuery = useQuery({
    queryKey: ["first-account-setup", "cmmd-projects"],
    queryFn: () => createCmmdApiClient().listProjects({ allWorkspaces: true }),
    staleTime: 30_000,
  });
  const initialUsageSource =
    settings.providers.codex.accessMode === "personal" ? "personal" : "workspace";
  const setupProjects: readonly SetupProjectRow[] = buildFirstAccountSetupProjects({
    forgeProjects: projects,
    cmmdProjects: cmmdProjectsQuery.data ?? [],
    includeForgeProjects,
    cmmdWorkspaces: cmmdWorkIntake.workspaces,
    environments: buildSetupEnvironments(serverConfig?.environment, savedEnvironmentsById),
  });
  const projectWorkspaceOptions = buildSetupProjectWorkspaceOptions({
    primaryEnvironment: includeForgeProjects ? serverConfig?.environment : null,
    cmmdWorkspaces: cmmdWorkIntake.workspaces,
  });

  const persistSetupState = (state: FirstAccountSetupState) =>
    persistFirstAccountSetupProgress({
      queryClient,
      state,
      updateLocalSettings: (firstAccountSetup) => updateSettings({ firstAccountSetup }),
      useCmmdSetupState,
    });

  const persistSetupCompletion = async (input: {
    readonly usageSource: "workspace" | "personal";
  }): Promise<boolean> => {
    if (useCmmdSetupState) {
      try {
        await updateCmmdForgeAccountPreferences({
          queryClient,
          preferences: { firstAccountSetup: COMPLETED_FIRST_ACCOUNT_SETUP_STATE },
        });
        return true;
      } catch (error) {
        toastManager.add({
          type: "error",
          title: "Could not save setup",
          description:
            error instanceof Error
              ? error.message
              : "Forge could not confirm setup completion. Try again.",
        });
        return false;
      }
    }

    return persistFirstAccountSetupCompletion({
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
      patch: buildFirstAccountSetupCompletionSettingsPatch({
        usageSource: input.usageSource,
        settings,
        // A cloud store credential counts as authenticated for access-mode
        // selection: the local CLI is never authenticated on a cloud surface.
        codexAuthenticated:
          codexProvider?.auth.status === "authenticated" ||
          cloudCodexCredential?.connected === true,
      }),
      updateServerSettings: getCurrentPrimaryServerApi().updateSettings,
      onServerSettingsPersisted: applySettingsUpdated,
      onError: (error) => {
        toastManager.add({
          type: "error",
          title: "Could not save setup",
          description:
            error instanceof Error
              ? error.message
              : "Forge could not confirm setup completion. Try again.",
        });
      },
    });
  };

  const persistSetupStep = (activeStepId: FirstAccountSetupStepId | "environment") => {
    if (activeStepId === "environment") {
      return;
    }
    persistSetupState({ status: "in_progress", activeStepId });
  };

  const handleUsageSourceChange = (source: "workspace" | "personal") => {
    if (source !== "workspace") {
      return;
    }
    if (useCmmdSetupState) {
      return;
    }
    updateSettings({
      providers: {
        ...settings.providers,
        codex: { ...settings.providers.codex, accessMode: "workspace" },
      },
    });
  };

  const handleGitHubConnect = async () => {
    persistSetupStep("capabilities");
    setGitHubActionBusy(true);
    try {
      const outcome = await pluginConnections.connect("github");
      if (outcome.kind === "failed" || outcome.kind === "not-available") {
        throw new Error(outcome.kind === "failed" ? outcome.message : "GitHub is not available");
      }
      void githubCapabilityQuery.refetch();
      if (githubFocusRefetchListenerRef.current) {
        window.removeEventListener("focus", githubFocusRefetchListenerRef.current);
      }
      const focusRefetchListener = (): void => {
        githubFocusRefetchListenerRef.current = null;
        void githubCapabilityQuery.refetch();
      };
      githubFocusRefetchListenerRef.current = focusRefetchListener;
      window.addEventListener("focus", focusRefetchListener, { once: true });
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not open GitHub",
        description: error instanceof Error ? error.message : "Try again from setup.",
      });
    } finally {
      setGitHubActionBusy(false);
    }
  };

  const handleOpenProjectImporter = (project?: SetupProjectRow) => {
    if (project?.source === "cmmd") {
      openAddProjectSourceMode("cmmd-project");
      return;
    }
    openAddProjectSources();
  };

  const handleSetupComplete = async (input: {
    readonly usageSource: "workspace" | "personal";
    readonly selectedProjectId: string | null;
  }) => {
    const selectedSetupProject =
      setupProjects.find((project) => project.id === input.selectedProjectId) ??
      setupProjects.find(isSetupProjectOpenable) ??
      null;
    const selectedProject = selectedSetupProject
      ? projects.find(
          (project) =>
            project.environmentId === selectedSetupProject.environmentId &&
            project.id === selectedSetupProject.projectId,
        )
      : null;

    const setupSaved = await persistSetupCompletion({ usageSource: input.usageSource });
    if (!setupSaved) {
      return;
    }

    if (selectedProject && selectedSetupProject?.source === "forge") {
      await startFirstAccountSetupProjectThread({
        startThread: async () => {
          await handleNewThread(
            scopeProjectRef(selectedProject.environmentId, selectedProject.id),
            {
              envMode: "local",
              initialPrompt: FIRST_ACCOUNT_SETUP_STARTER_PROMPT,
              worktreePath: selectedSetupProject?.cwd ?? selectedProject.cwd,
            },
          );
        },
        onThreadStarted: persistFirstAccountSetupStarterPrompt,
        onFallback: navigateToFirstAccountSetupStarterPrompt,
      });
      return;
    }

    navigateToFirstAccountSetupStarterPrompt();
  };

  return (
    <AppTopBarShell>
      <FirstAccountSetup
        accountLabel={null}
        theme={theme}
        uiFontSize={uiFontSize}
        codeFontSize={codeFontSize}
        sidebarLayoutMode={settings.sidebarLayoutMode}
        {...firstRunEnvironmentStep}
        initialUsageSource={initialUsageSource}
        providers={serverConfig?.providers ?? []}
        projects={setupProjects}
        projectWorkspaceOptions={projectWorkspaceOptions}
        projectsLoading={cmmdProjectsQuery.isLoading || cmmdWorkIntake.isLoading}
        githubCapability={githubCapabilityQuery.data ?? null}
        githubCapabilityLoading={githubCapabilityQuery.isLoading}
        githubActionBusy={githubActionBusy}
        githubAuthorizeUrl={null}
        githubAuthorizeUrlLoading={false}
        plugins={pluginConnections.plugins}
        pluginsLoading={pluginConnections.loading}
        pluginsError={pluginConnections.error}
        pluginConnectionAttempt={pluginConnections.attempts.github ?? null}
        pluginConnectionAttempts={pluginConnections.attempts}
        providerActionBusy={providerAuth.actionBusy}
        providerActionLabel={providerAuth.actionLabel}
        providerActionProviderId={providerAuth.actionProviderId}
        providerCodePrompt={providerAuth.codePrompt}
        providerAuthorizeUrl={providerAuth.authorizeUrl}
        providerNeedsRuntime={providerAuth.needsRuntime}
        cloudCodexCredential={cloudCodexCredential}
        cloudClaudeCredential={cloudClaudeCredential}
        codexCloudSignInSlot={
          codexCloudSignInActive ? (
            <CloudProviderSignInView controller={codexCloudSignIn} provider="codex" />
          ) : null
        }
        claudeCloudSignInSlot={
          claudeCloudSignInActive ? (
            <CloudProviderSignInView controller={claudeCloudSignIn} provider="claudeAgent" />
          ) : null
        }
        workspaceLabel={serverConfig?.environment.label ?? null}
        onComplete={(input) => void handleSetupComplete(input)}
        onGitHubConnect={() => void handleGitHubConnect()}
        onPluginConnect={(pluginId) => void pluginConnections.connect(pluginId)}
        onOpenProjectImporter={handleOpenProjectImporter}
        onProviderConnect={(providerId) =>
          routeOnboardingProviderConnect({
            providerId,
            codexCloudSignInActive,
            claudeCloudSignInActive,
            startCloudCodex: () => codexCloudSignIn.start("codex"),
            startCloudClaude: () => claudeCloudSignIn.start("claudeAgent"),
            connectLocal: providerAuth.connect,
          })
        }
        onSubmitProviderCode={(code) => providerAuth.submitCode(code)}
        onThemeChange={setTheme}
        onUiFontSizeChange={setUiFontSize}
        onCodeFontSizeChange={setCodeFontSize}
        onSidebarLayoutModeChange={(mode) => updateSettings({ sidebarLayoutMode: mode })}
        onStepChange={persistSetupStep}
        onUsageSourceChange={handleUsageSourceChange}
      />
    </AppTopBarShell>
  );
}
